import { EventBus } from "../../../helpers/EventBus";
import { appEnv } from "../../../config/env";
import { CacheRefreshProvider } from "./CacheRefreshProvider";
import { useEffect, useRef, useState } from "react";
import AdminFooter from "./AdminFooter";
import AdminHeader from "./AdminHeader";
import { ActionEmitter, actionEmitter } from "../../../config/ActionEmitter";
import ConfirmProvider from "../../../config/ConfirmProvider";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleExclamation, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Button } from "react-bootstrap";
import AdminPageIntro from "./AdminPageIntro";
import { getAdminPageIntroConfig } from "./adminPageIntroConfig";
import Breadcrumbs from "./Breadcrumb";
import { CentralSiteAccess } from "../../../frontend/user/CentralSiteAccess";


interface AdminLayoutProps {
    children: React.ReactNode;
}

interface SystemMessage {
  id: number;
  success: boolean;
  message: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
    const location = useLocation();
    const [siteId, setSiteId] = useState(() => localStorage.getItem(appEnv.storageCurrentSiteKey));
    useEffect(() => {
      const selected = (id: string) => setSiteId(id);
      const bus = EventBus.getInstance();
      bus.subscribe(EventBus.Event.OnSiteSelected, selected);
      return () => bus.unsubscribe(EventBus.Event.OnSiteSelected, selected);
    }, []);

    const [systemMessages, setSystemMessages] = useState<Array<SystemMessage>>([]);

    const eventRef = useRef<ActionEmitter>();
    const hideMessageTimeoutRef = useRef<Record<number, number>>({});
    const nextMessageIdRef = useRef<number>(1);
    const isDesignerRoute = location.pathname.toLowerCase().startsWith("/content/design/");
    const isWorkspaceRoute = isDesignerRoute;
    const isContentHomeRoute = location.pathname.toLowerCase() === "/content";
    const pageIntro = !isWorkspaceRoute && !isContentHomeRoute ? getAdminPageIntroConfig(location.pathname) : null;
    const hidePageIntro = ["/account/details", "/content/mediamanager", "/systemadmin/components", "/systemadmin/propertytypes", "/systemadmin/layouttemplates"].includes(location.pathname.toLowerCase().replace(/\/$/, ""));

    const removeMessage = (messageId: number): void => {
      const timeout = hideMessageTimeoutRef.current[messageId];

      if (timeout) {
        window.clearTimeout(timeout);
        delete hideMessageTimeoutRef.current[messageId];
      }

      setSystemMessages(prev => prev.filter(message => message.id !== messageId));
    };

    useEffect(() => {
      if (!eventRef.current) {
        eventRef.current = actionEmitter;

        eventRef.current.on("RESULT", (success: boolean, message: string) => {
          const messageId = nextMessageIdRef.current++;

          setSystemMessages(prev => [...prev, { id: messageId, success, message }]);

          hideMessageTimeoutRef.current[messageId] = window.setTimeout(() => {
            removeMessage(messageId);
          }, success ? 3200 : 5200);
        });
      }

      return () => {
        Object.values(hideMessageTimeoutRef.current).forEach(timeout => window.clearTimeout(timeout));
        hideMessageTimeoutRef.current = {};
      };
    }, []);

    return (
      <CacheRefreshProvider>
      <div className={`admin-shell ${isWorkspaceRoute ? "admin-shell--workspace" : ""}`}>
        {systemMessages.length > 0 && (
          <div className="global-notification-stack" aria-live="polite" aria-atomic="false">
            {systemMessages.map(systemMessage => (
              <div
                key={systemMessage.id}
                className={`global-notification global-notification--${systemMessage.success ? "success" : "danger"}`}
              >
                <div className="global-notification__icon">
                  <FontAwesomeIcon icon={systemMessage.success ? faCircleCheck : faCircleExclamation} />
                </div>
                <div className="global-notification__content">
                  <div className="global-notification__title">{systemMessage.success ? "Saved" : "Attention needed"}</div>
                  <div className="global-notification__message">{systemMessage.message}</div>
                </div>
                <Button
                  type="button"
                  variant="link"
                  className="global-notification__close"
                  onClick={() => removeMessage(systemMessage.id)}
                  aria-label="Dismiss notification"
                  title="Dismiss notification"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </Button>
              </div>
            ))}
          </div>
        )}
        <AdminHeader />
        <div className={`admin-shell__body ${isWorkspaceRoute ? "admin-shell__body--workspace" : ""}`}>
          <div className={`content admin-shell__content m-0 p-0 ${isWorkspaceRoute ? "content--fullheight admin-shell__content--workspace" : "admin-shell__content--standard"}`} id="SiteAdminContent">
            <ConfirmProvider>
              <div className={`admin-page-frame ${isWorkspaceRoute ? "admin-page-frame--fullheight admin-page-frame--workspace" : ""}`}>
                {pageIntro?.breadcrumbs?.length ? <Breadcrumbs Items={pageIntro.breadcrumbs} /> : null}
                {pageIntro && !hidePageIntro && <AdminPageIntro {...pageIntro} />}
                <div className="admin-page-frame__body">
                  <CentralSiteAccess key={siteId}>{children}</CentralSiteAccess>
                </div>
              </div>
            </ConfirmProvider>
          </div>
          {!isDesignerRoute && (
            <footer className="admin-shell__footer">
              <AdminFooter />  {/* Common footer */}
            </footer>
          )}
        </div>
      </div>
      </CacheRefreshProvider>
    );
  };

  export default AdminLayout;
